import {
  analyzeDocument,
  type AnalyzedDocument,
  DEFAULT_LINT_OPTIONS,
  type LintOptions,
} from '@jsonfmt/core';

/**
 * The last `AnalyzedDocument`, kept so that lint → repair → format over one text runs the
 * pipeline once (ADR-0010).
 *
 * ADR-0010 says "keyed by `version`". A version alone is not enough as a key and the reason
 * is `repair`: the repair driver re-analyses its own intermediate texts, at the same
 * document version, once per pass. Keying on version alone would hand pass 2 the analysis
 * of the text as it was before pass 1 — a wrong answer, silently. So the key is
 * `(version, source, lint options)`, with the version still in it because it is the cheapest
 * discriminator and because a text that comes back byte-identical at a NEW version really
 * is a different document as far as the editor is concerned.
 *
 * One entry, not an LRU. The access pattern is "the same text three times in a row"; a map
 * would hold megabytes of token streams for documents the user has already edited past.
 *
 * ONE COPY, TWO HOMES. This module is imported by `analyze.worker.ts` and by the inline
 * transport in `../analyzer.ts`. That is deliberate: `analyzer.contract.test.ts` asserts
 * that both paths produce identical output, and the cheapest way to keep that true is for
 * both paths to be the same code reached through different transports.
 */

/**
 * Every field of `LintOptions` that changes the result, as a compile-time checklist.
 *
 * `Record<keyof LintOptions, true>` means adding a field to `LintOptions` and forgetting it
 * here is a type error in this file — not a stale cache entry discovered by a user whose
 * `detectDuplicateKeys` toggle appeared to do nothing.
 */
const CACHE_KEY_FIELDS: Record<keyof LintOptions, true> = {
  maxDepth: true,
  maxDiagnostics: true,
  maxBytes: true,
  detectDuplicateKeys: true,
  detectPrecisionLoss: true,
};

const KEY_FIELD_NAMES: readonly (keyof LintOptions)[] = (
  Object.keys(CACHE_KEY_FIELDS) as (keyof LintOptions)[]
).sort();

function optionsKey(options?: Partial<LintOptions>): string {
  const resolved: LintOptions = { ...DEFAULT_LINT_OPTIONS, ...options };
  return KEY_FIELD_NAMES.map((name) => `${name}=${String(resolved[name])}`).join('&');
}

export interface AnalysisCache {
  /** The pipeline, memoised on `(version, source, options)`. */
  analyze(version: number, source: string, options?: Partial<LintOptions>): AnalyzedDocument;
  /**
   * The same thing shaped as `(source) => AnalyzedDocument`, which is what
   * `createRepairEngine({ analyze })` takes. The version is pinned; the source is not.
   */
  boundTo(version: number, options?: Partial<LintOptions>): (source: string) => AnalyzedDocument;
  /** Drops the entry. Only `dispose()` needs this; it exists so the memory is releasable. */
  clear(): void;
  /** Test-visible: how many of the calls so far were served without running the pipeline. */
  readonly hits: number;
}

interface Entry {
  readonly version: number;
  readonly source: string;
  readonly key: string;
  readonly doc: AnalyzedDocument;
}

export function createAnalysisCache(): AnalysisCache {
  let entry: Entry | null = null;
  let hits = 0;

  function analyze(
    version: number,
    source: string,
    options?: Partial<LintOptions>,
  ): AnalyzedDocument {
    const key = optionsKey(options);
    if (
      entry !== null &&
      entry.version === version &&
      entry.key === key &&
      entry.source === source
    ) {
      hits += 1;
      return entry.doc;
    }
    const doc = analyzeDocument(source, options);
    entry = { version, source, key, doc };
    return doc;
  }

  return {
    analyze,
    boundTo(version, options) {
      return (source) => analyze(version, source, options);
    },
    clear() {
      entry = null;
    },
    get hits() {
      return hits;
    },
  };
}
